'use client';

import cardValidator from 'card-validator';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { isEncryptedCardMask } from '@sola/shared-utils/credit-cards.js';

import { InputField, InputFieldProps } from '../input-field/input-field';
import {
  removeNonDigits,
  resetAchRoutingAndAccount,
  shouldRemoveSpecialChars,
} from './utils';

type ACHNumberFieldProps = Omit<
  InputFieldProps,
  | 'maskOptions'
  | 'placeholder'
  | 'prefix'
  | 'type'
  | 'minLength'
  | 'maxLength'
  | 'pattern'
  | 'aria-label'
  | 'aria-labelledby'
> & {
  name: string;
};

export const ACHAccountNumber: React.FC<ACHNumberFieldProps> = ({
  ...fieldProps
}) => {
  const { name } = fieldProps;
  const { t } = useTranslation(['checkout']);
  const { setValue, getValues } = useFormContext();
  const formContext = useFormContext();

  const MIN_LENGTH = 4;
  const MAX_LENGTH = 17;

  const clearValues = () => resetAchRoutingAndAccount(formContext);

  return (
    <InputField
      autoComplete="off"
      label={t('paymentMethods.ach.fields.account')}
      inputMode="numeric"
      // minLength={MIN_LENGTH}
      // maxLength={MAX_LENGTH}
      onBlur={() => {
        setValue(name, getValues(name).trim(), {
          shouldValidate: true,
        });
      }}
      onChange={() => {
        const newVal = getValues(name);
        if (shouldRemoveSpecialChars(newVal)) {
          setValue(name, removeNonDigits(newVal));
        }
      }}
      onFocus={(e) => {
        if (isEncryptedCardMask(e.target.value)) {
          clearValues();
        }
      }}
      validate={(value: string) => {
        if (isEncryptedCardMask(value)) {
          return true;
        }

        if (value.length < MIN_LENGTH || value.length > MAX_LENGTH) {
          return t('checkout:form.ach.accountInvalid');
        }

        if (formContext.getValues().achRoutingNumber === value) {
          return t('checkout:form.ach.accountRoutingMatch');
        }

        if (cardValidator.number(value).isValid) {
          return t('checkout:form.ach.accountNumber');
        }

        return (
          /^(\d)+((-(\d)+)|( (\d)+)){0,}$/.test(value) ||
          t('checkout:form.ach.accountInvalid')
        );
      }}
      {...fieldProps}
    />
  );
};
